/** Lists the main rail items the person is already authorised to see, with a switch each. */

import React from 'react';

import { Button } from '../../shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../shared/ui/dialog';
import { Switch } from '../../shared/ui/switch';
import { MAIN_RAIL_PINNED_IDS } from './useMainRailPreferences';

export type CustomiseRailItem = { id: string; label: string; icon?: React.ElementType };

export function CustomiseRailDialog({
  open,
  onOpenChange,
  items,
  hiddenSet,
  onToggle,
  onReset,
  canReset,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: readonly CustomiseRailItem[];
  hiddenSet: ReadonlySet<string>;
  onToggle: (id: string, hidden: boolean) => void;
  onReset: () => void;
  /** Defaults to "something is hidden"; callers that also track order pass their own. */
  canReset?: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Customise rail</DialogTitle>
          <DialogDescription>Choose which items appear in the main rail.</DialogDescription>
        </DialogHeader>
        <ul className="max-h-[50vh] overflow-y-auto -mx-1 px-1" data-slot="customise-rail-list">
          {items.map((item) => {
            const Icon = item.icon;
            const pinned = MAIN_RAIL_PINNED_IDS.includes(item.id);
            const labelId = `customise-rail-${item.id}`;
            return (
              <li key={item.id} className="flex items-center gap-3 py-2">
                {Icon ? <Icon className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" /> : <span className="size-5 shrink-0" />}
                <span id={labelId} className="min-w-0 flex-1 truncate text-sm">{item.label}</span>
                <Switch
                  aria-labelledby={labelId}
                  checked={pinned || !hiddenSet.has(item.id)}
                  disabled={pinned}
                  onCheckedChange={(checked: boolean) => onToggle(item.id, !checked)}
                />
              </li>
            );
          })}
        </ul>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={!(canReset ?? hiddenSet.size > 0)} onClick={onReset}>
            Reset to default
          </Button>
          <Button type="button" onClick={() => onOpenChange(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
